import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Button,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  Card,
  CardActionArea,
  CardContent,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HeadphonesIcon from "@mui/icons-material/Headphones";
import mermaid from "mermaid";
import { useLearningArticle, useLearningArticles } from "../../../../hooks/useLearningArticles";
import { parseLearningArticleMarkdown } from "./learningArticleMarked";
import { ArticleImageLightbox } from "./ArticleImageLightbox";
import { SECTION_MAP } from "./sections";
import PHASES from "./workflowData";

const allSteps = PHASES.flatMap((p) => p.steps);

function getStepLabel(num) {
  const step = allSteps.find((s) => s.number === num);
  return step ? `Step ${num}: ${step.title}` : `Step ${num}`;
}

function getPhaseForStep(num) {
  return PHASES.find((p) => p.steps.some((s) => s.number === num));
}

export default function LearnArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { data: article, isLoading, error } = useLearningArticle(slug);
  const { data: allArticles = [] } = useLearningArticles();
  const articleContentRef = useRef(null);
  const [imageLightbox, setImageLightbox] = useState(null);

  const closeImageLightbox = useCallback(() => setImageLightbox(null), []);

  const htmlContent = useMemo(
    () => parseLearningArticleMarkdown(article?.content),
    [article?.content],
  );

  useEffect(() => {
    if (!htmlContent || !articleContentRef.current) return;

    const mermaidBlocks = articleContentRef.current.querySelectorAll(".mermaid");
    if (!mermaidBlocks.length) return;

    mermaid.initialize({
      startOnLoad: false,
      securityLevel: "strict",
      theme: "default",
    });

    mermaid.run({ nodes: Array.from(mermaidBlocks) }).catch((err) => {
      // Keep article readable even when diagram rendering fails.
      console.error("Failed to render mermaid diagram:", err);
    });
  }, [htmlContent]);

  useEffect(() => {
    const el = articleContentRef.current;
    if (!el || !htmlContent) return;

    const onClick = (e) => {
      const t = e.target;
      if (t.tagName !== "IMG") return;
      e.preventDefault();
      e.stopPropagation();
      setImageLightbox({
        src: t.currentSrc || t.src,
        alt: t.alt || "",
      });
    };

    el.addEventListener("click", onClick);
    return () => el.removeEventListener("click", onClick);
  }, [htmlContent]);

  const relatedArticles = useMemo(() => {
    if (!article?.related_article_slugs?.length || !allArticles.length) return [];
    return article.related_article_slugs
      .map((s) => allArticles.find((a) => a.slug === s))
      .filter(Boolean);
  }, [article?.related_article_slugs, allArticles]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !article) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Article not found. It may have been moved or is no longer available.
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/home/learn")}
          sx={{ mt: 2 }}
        >
          Back to Help Center
        </Button>
      </Box>
    );
  }

  const section = SECTION_MAP[article.section_id];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 800, mx: "auto" }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2, fontSize: "0.8rem" }}>
        <Link
          underline="hover"
          color="inherit"
          sx={{ cursor: "pointer" }}
          onClick={() => navigate("/home/learn")}
        >
          Help Center
        </Link>
        {section && (
          <Typography color="text.secondary" sx={{ fontSize: "0.8rem" }}>
            {section.label}
          </Typography>
        )}
        <Typography color="text.primary" sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
          {article.title}
        </Typography>
      </Breadcrumbs>

      {/* Title */}
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
        {article.title}
      </Typography>

      {/* Meta row */}
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
        {article.read_time_minutes && (
          <Chip
            icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
            label={`${article.read_time_minutes} min read`}
            size="small"
            variant="outlined"
            sx={{ height: 24 }}
          />
        )}
        {section && (
          <Chip label={section.label} size="small" sx={{ height: 24 }} />
        )}
      </Stack>

      {/* Listen and Learn audio player */}
      {article.listen && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            mb: 2,
            p: 1,
            borderRadius: 2,
            bgcolor: "#6366f108",
            border: "1px solid #6366f120",
          }}
        >
          <HeadphonesIcon sx={{ fontSize: 18, color: "#6366f1", flexShrink: 0 }} />
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: "#6366f1", whiteSpace: "nowrap" }}
          >
            Listen and Learn
          </Typography>
          <Box
            key={article.listen}
            component="audio"
            controls
            preload="none"
            src={article.listen}
            sx={{ height: 32, flex: 1, minWidth: 0 }}
          />
        </Stack>
      )}

      {/* Workflow context */}
      {article.workflow_steps?.length > 0 && (
        <Box
          sx={{
            mb: 3,
            p: 1.5,
            borderRadius: 2,
            bgcolor: "action.hover",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
            Part of the Coaching Workflow
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {article.workflow_steps.map((num) => {
              const phase = getPhaseForStep(num);
              return (
                <Chip
                  key={num}
                  label={getStepLabel(num)}
                  size="small"
                  sx={{
                    bgcolor: phase ? `${phase.color}15` : undefined,
                    borderColor: phase?.color,
                    fontWeight: 500,
                    fontSize: "0.7rem",
                    height: 24,
                  }}
                  variant="outlined"
                />
              );
            })}
          </Stack>
        </Box>
      )}

      {/* Article content */}
      <Box
        ref={articleContentRef}
        sx={{
          "& h1": { fontSize: "1.5rem", fontWeight: 700, mt: 3, mb: 1 },
          "& h2": { fontSize: "1.25rem", fontWeight: 600, mt: 2.5, mb: 1 },
          "& h3": { fontSize: "1.1rem", fontWeight: 600, mt: 2, mb: 0.75 },
          "& p": { lineHeight: 1.7, mb: 1.5, color: "text.secondary" },
          "& ul, & ol": { pl: 3, mb: 1.5, color: "text.secondary" },
          "& li": { mb: 0.5, lineHeight: 1.6 },
          "& code": {
            bgcolor: "action.hover",
            px: 0.75,
            py: 0.25,
            borderRadius: 0.5,
            fontSize: "0.85em",
          },
          "& blockquote": {
            borderLeft: "3px solid",
            borderColor: "primary.main",
            pl: 2,
            ml: 0,
            my: 2,
            color: "text.secondary",
            fontStyle: "italic",
          },
          "& img": {
            maxWidth: "100%",
            height: "auto",
            display: "block",
            borderRadius: 1,
            my: 2,
            boxShadow: "0 1px 6px rgba(0,0,0,0.08)",
            cursor: "zoom-in",
          },
          "& video.learn-article-video": {
            maxWidth: "100%",
            height: "auto",
            display: "block",
            borderRadius: 1,
            my: 2,
            boxShadow: "0 1px 6px rgba(0,0,0,0.08)",
            backgroundColor: "#000",
          },
          "& a": { color: "primary.main" },
          "& .mermaid": {
            overflowX: "auto",
            my: 2,
          },
          "& table": {
            width: "100%",
            borderCollapse: "collapse",
            mb: 2,
            "& th, & td": {
              border: "1px solid",
              borderColor: "divider",
              p: 1,
              fontSize: "0.85rem",
              textAlign: "left",
            },
            "& th": { bgcolor: "action.hover", fontWeight: 600 },
          },
        }}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />

      {/* Go to feature */}
      {article.app_route && (
        <>
          <Divider sx={{ my: 3 }} />
          <Button
            variant="outlined"
            endIcon={<OpenInNewIcon />}
            onClick={() => navigate(article.app_route)}
            sx={{ textTransform: "none" }}
          >
            Go to this feature in the app
          </Button>
        </>
      )}

      {/* Related articles */}
      {relatedArticles.length > 0 && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
            Related Articles
          </Typography>
          <Stack spacing={1}>
            {relatedArticles.map((ra) => (
              <Card key={ra.slug} variant="outlined" sx={{ borderRadius: 2 }}>
                <CardActionArea onClick={() => navigate(`/home/learn/${ra.slug}`)}>
                  <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {ra.title}
                    </Typography>
                    {ra.excerpt && (
                      <Typography variant="caption" color="text.secondary">
                        {ra.excerpt}
                      </Typography>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Stack>
        </>
      )}

      {/* Back button */}
      <Box sx={{ mt: 4, mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/home/learn")}
          sx={{ textTransform: "none" }}
        >
          Back to Help Center
        </Button>
      </Box>

      <ArticleImageLightbox
        open={Boolean(imageLightbox)}
        src={imageLightbox?.src}
        alt={imageLightbox?.alt}
        onClose={closeImageLightbox}
      />
    </Box>
  );
}
